import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  map,
  Observable,
  tap,
  shareReplay,
  take,
  filter,
  of,
  switchMap,
} from 'rxjs';
import { ProfilePicture, User, ManagedUser } from '../model/user';
import { CompanyInvitation, CompanyUser } from '@/model/company';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { Store } from '@ngrx/store';
import * as UserActions from '@/app/store/user/user.actions';
import * as UserSelectors from '@/app/store/user/user.selectors';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = `${environment.api}/graphql`;
  private apiRootUrl = `${environment.api}`;
  private cacheKey = 'sondis_user_cache';

  // Create BehaviorSubjects for both user types
  private userSubject = new BehaviorSubject<User | null>(null);
  private kcUserSubject = new BehaviorSubject<KeycloakProfile | null>(null);
  private managedUsersSubject = new BehaviorSubject<ManagedUser[]>([]);

  // Public observables for components to subscribe to
  public user$ = this.userSubject.asObservable();
  public kcUser$ = this.kcUserSubject.asObservable();
  public managedUsers$ = this.managedUsersSubject.asObservable();

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private store: Store,
  ) {
    this.initializeUserData();
  }

  private initializeUserData() {
    // Load Keycloak user profile
    this.keycloakService.loadUserProfile().then((user) => {
      this.kcUserSubject.next(user);
    });

    // Load application user data
    this.loadUserInfo().subscribe();
  }

  // Private method to load user info from API
  private loadUserInfo(): Observable<User> {
    // Dispatch the load user action
    this.store.dispatch(UserActions.loadUser());

    // Return an observable from the store
    return this.store.select(UserSelectors.selectUser).pipe(
      filter((user) => !!user),
      tap((user) => this.userSubject.next(user)),
      map((user) => user as User), // Safe cast since we filtered out null
      take(1),
    );
  }

  // Public method to force refresh user data (bypasses cache)
  public refreshUserInfo(): Observable<User> {
    console.log('🔄 UserService: refreshUserInfo() called');

    // Clear cache, store, and subject to force fresh API call
    this.clearUserCache();
    this.userSubject.next(null);
    this.store.dispatch(UserActions.clearUser()); // Reset store to initial state
    console.log(
      '🔄 UserService: Cache, subject, and store cleared, dispatching loadUser',
    );

    // Dispatch loadUser which will now make a fresh API call
    this.store.dispatch(UserActions.loadUser());

    // Return observable that waits for the fresh data
    return this.store.select(UserSelectors.selectUserLoading).pipe(
      map((loading) => {
        console.log('🔄 UserService: Loading state:', loading);
        return loading;
      }),
      filter((loading) => !loading),
      switchMap(() => this.store.select(UserSelectors.selectUser)),
      map((user) => {
        console.log('🔄 UserService: Fresh user data from store:', user);
        return user;
      }),
      filter((user) => !!user),
      map((user) => user as User),
      take(1),
    );
  }

  // Synchronous getter for current user value
  getUser(): User | null {
    return this.userSubject.getValue();
  }

  // Synchronous getter for current KC user value
  getKcUser(): KeycloakProfile | null {
    return this.kcUserSubject.getValue();
  }

  // Method to get user info, now returns cached data or fetches if needed
  getUserInfo(): Observable<User> {
    // Check if user exists in store
    return this.store.select(UserSelectors.selectUser).pipe(
      take(1),
      switchMap((storeUser) => {
        // If user exists in store, use it
        if (storeUser) {
          return of(storeUser);
        }

        // If not in store, check if we have it in the subject
        const userFromSubject = this.userSubject.getValue();
        if (userFromSubject) {
          return of(userFromSubject);
        }

        // If we don't have it anywhere, make the API call by dispatching action
        this.store.dispatch(UserActions.loadUser());

        // And return an observable that completes when the user is loaded
        return this.store.select(UserSelectors.selectUser).pipe(
          filter((user) => !!user),
          map((user) => user as User), // Safe cast since we filtered out null
          take(1),
        );
      }),
    );
  }

  // Method specifically for guards - can return null users for onboarding flow
  getUserInfoForGuard(): Observable<User | null> {
    // Always dispatch loadUser to ensure background refresh happens
    // The effect will handle cached vs fresh data appropriately
    this.store.dispatch(UserActions.loadUser());

    // Wait for loading to complete and return the result
    return this.store.select(UserSelectors.selectUserLoading).pipe(
      filter((loading) => !loading),
      switchMap(() => this.store.select(UserSelectors.selectUser)),
      take(1),
    );
  }

  // Method to update the cached user data
  updateCachedUser(user: User) {
    this.userSubject.next(user);
    // Also update the store
    this.store.dispatch(UserActions.loadUserSuccess({ user }));
  }

  clearCachedUser() {
    this.userSubject.next(null);
    // Also clear localStorage cache
    this.clearUserCache();
  }

  // Clear user cache from localStorage (call on logout)
  clearUserCache(): void {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch {
      // Ignore errors if localStorage is unavailable
    }
  }

  // In graphql.service.ts

  getAllUsers(page = 0, size = 9999): Observable<User[]> {
    const query = `
    query {
      getAllUser(page: ${page}, size: ${size}) {
        id
        isRegionalAdvisoryboard
        companyName
        zipCode
        address {
            city
            country
            plz
            state
            street
        }
        company {
          email
          companyName
          contactPersonName
          website
          phone
          categoryIds
          acceptedPrivacyPolicy
          uid
        }
        email
        firstName
        isAdvisoryboard
        lastName
        birthYear
        roles
        advisoryCategories
        advisoryExcludedBezirkIds
      }
    }
  `;

    return this.http
      .post<{
        data: {
          getAllUser: User[];
        };
      }>(this.apiUrl, { query })
      .pipe(map((response) => response.data.getAllUser));
  }

  deleteUser(userId: string): Observable<User> {
    this.clearUserCache(); // Clear cache when deleting user
    const mutation = `
    mutation {
      deleteUser(id: "${userId}")
    }
    `;

    return this.http
      .post<{
        data: {
          deleteUser: User;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(map((response) => response.data.deleteUser));
  }

  deleteManagedUser(userId: string): Observable<ManagedUser> {
    this.clearManagedUsersCache();
    const mutation = `
    mutation {
      deleteManagedUser(id: "${userId}")
    }
  `;

    return this.http
      .post<{
        data: {
          deleteManagedUser: ManagedUser;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(map((response) => response.data.deleteManagedUser));
  }
  addUser(user: Partial<User>): Observable<User> {
    this.clearUserCache(); // Clear cache when adding user
    const addressParts = [];
    if (user.address?.city) addressParts.push(`city: "${user.address.city}"`);
    if (user.address?.country)
      addressParts.push(`country: "${user.address.country}"`);
    if (user.address?.plz) addressParts.push(`plz: ${user.address.plz}`);
    if (user.address?.state)
      addressParts.push(`state: "${user.address.state}"`);
    if (user.address?.street)
      addressParts.push(`street: "${user.address.street}"`);

    const address =
      addressParts.length > 0 ? `address: { ${addressParts.join(' ')} }` : '';
    const birthdate = user.birthdate ? `birthdate: "${user.birthdate}"` : '';
    const managerId = user.managerId ? `managerId: "${user.managerId}"` : '';
    const categoryIds =
      user.categoryIds && user.categoryIds.length > 0
        ? `categoryIds: [${user.categoryIds.map((id) => `"${id}"`).join(', ')}]`
        : '';
    const roles =
      typeof user.roles === 'string'
        ? `roles: ["${user.roles}"]`
        : user.roles && user.roles.length > 0
          ? `roles: [${user.roles.map((role) => `"${role}"`).join(', ')}]`
          : '';
    const advisoryCategories =
      user.advisoryCategories && user.advisoryCategories.length > 0
        ? `advisoryCategories: [${user.advisoryCategories
            .map((id) => `"${id}"`)
            .join(', ')}]`
        : '';

    const mutation = `
    mutation {
      createUser(
        email: "${user.email || ''}"
        companyName: "${user.companyName || ''}"
        firstName: "${user.firstName || ''}"
        lastName: "${user.lastName || ''}"
        isAdvisoryboard: ${user.isAdvisoryboard || false}
        isRegionalAdvisoryboard: ${user.isRegionalAdvisoryboard || false}
        ${birthdate}
        ${managerId}
        ${address}
        ${categoryIds}
        ${roles}
        ${advisoryCategories}
        ${user.advisoryExcludedBezirkIds && user.advisoryExcludedBezirkIds.length > 0 ? `advisoryExcludedBezirkIds: [${user.advisoryExcludedBezirkIds.join(', ')}]` : 'advisoryExcludedBezirkIds: []'}
      ) {
        id
        firstName
        lastName
        email
        companyName
        isAdvisoryboard
        isRegionalAdvisoryboard
        birthdate
        managerId
        address {
          id
          city
          country
          plz
          state
          street
        }
        roles
        advisoryCategories
        advisoryExcludedBezirkIds
      }
    }
  `;

    return this.http
      .post<{
        data: {
          createUser: User;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(map((response) => response.data.createUser));
  }

  updateUserCategories(user: User, categoryIds: string[]): Observable<User> {
    this.clearUserCache(); // Clear cache when updating user
    const mutation = `
    mutation {
      updateUser(
        id: "${user.id}"
        categoryIds: [${categoryIds.map((id) => `"${id}"`).join(', ')}]
      ) {
        id
        categories {
          id
        }
      }
    }
    `;

    console.log(mutation);

    return this.http
      .post<{
        data: {
          updateUser: User;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(map((response) => response.data.updateUser));
  }

  updateUserPersonalSettings(user: Partial<User>): Observable<User> {
    this.clearUserCache(); // Clear cache when updating user
    const mutation = `
    mutation {
      updateUser(
        id: "${user.id}"
        firstName: "${user.firstName}"
        lastName: "${user.lastName}"
        gender: "${user.gender}"
        birthYear: "${user.birthYear}"
        zipCode: "${user.zipCode}"
        profilePicture: "${user.profilePicture?.url || ''}"
      ) {
        id
        firstName
        lastName
        gender
        birthYear
        zipCode
        profilePicture
        }
      }
    `;

    console.log(mutation);

    return this.http
      .post<{
        data: {
          updateUser: User;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(
        map((response) => {
          console.log(response);
          return response.data.updateUser;
        }),
      );
  }

  updateUser(user: Partial<User>): Observable<User> {
    this.clearUserCache(); // Clear cache when updating user
    // Dynamischer Aufbau der Rollen- und Kategorien-Strings
    const advisoryCategories =
      user.advisoryCategories?.map((cat) => `"${cat}"`).join(', ') || '';
    const roles = user.roles
      ? `roles: [${user.roles.map((role) => `"${role}"`).join(', ')}]`
      : '';

    // Mutation string dynamisch zusammenstellen
    const mutation = `
    mutation {
      updateUser(
        id: "${user.id}"
        firstName: "${user.firstName}"
        lastName: "${user.lastName}"
        email: "${user.email}"
        birthYear: "${user.birthYear}"
        isAdvisoryboard: ${user.isAdvisoryboard}
        isRegionalAdvisoryboard: ${user.isRegionalAdvisoryboard}
        advisoryCategories: [${advisoryCategories}]
        ${roles}
        ${user.advisoryExcludedBezirkIds && user.advisoryExcludedBezirkIds.length > 0 ? `advisoryExcludedBezirkIds: [${user.advisoryExcludedBezirkIds.join(', ')}]` : 'advisoryExcludedBezirkIds: []'}
      ) {
        id
        firstName
        lastName
        email
        birthYear
        isAdvisoryboard
        isRegionalAdvisoryboard
        advisoryCategories
        roles
        advisoryExcludedBezirkIds
      }
    }
  `;

    console.log(mutation);

    return this.http
      .post<{
        data: {
          updateUser: User;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(
        map((response) => {
          console.log(response);
          return response.data.updateUser;
        }),
      );
  }

  createUser(user: Partial<User>): Observable<User> {
    this.clearUserCache(); // Clear cache when creating user
    return this.http.post<User>(`${this.apiRootUrl}/users/create`, user);
  }

  uploadProfilePicture(file: File): Observable<ProfilePicture> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ProfilePicture>(
        `${this.apiRootUrl}/users/upload/profil-picture`,
        formData,
      )
      .pipe(
        map((response) => response),
        tap(() => this.refreshUserInfo()),
      );
  }

  uploadManagedUserProfilePicture(
    managedUser: ManagedUser,
    file: File,
  ): Observable<ProfilePicture> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ProfilePicture>(
        `${this.apiRootUrl}/users/managed/${managedUser.id}/profile-picture`,
        formData,
      )
      .pipe(
        map((response) => response),
        tap(() => {
          // Clear the managed users cache to force a refresh
          this.clearManagedUsersCache();
        }),
      );
  }

  sendCompanyInvitation(
    companyInvitation: CompanyInvitation,
  ): Observable<CompanyInvitation> {
    this.clearUserCache(); // Clear cache when sending invitation
    return this.http.post<CompanyInvitation>(
      `${this.apiRootUrl}/companies/invite`,
      companyInvitation,
    );
  }

  resendCompanyInvitation(
    companyInvitation: CompanyInvitation,
  ): Observable<CompanyInvitation> {
    return this.http.post<CompanyInvitation>(
      `${this.apiRootUrl}/companies/reinvite`,
      {
        email: companyInvitation.email,
        redirectUri: `${window.location.origin}/`,
      },
    );
  }

  deleteCompanyInvitation(
    companyInvitation: CompanyInvitation,
  ): Observable<CompanyInvitation> {
    return this.http.delete<CompanyInvitation>(
      `${this.apiRootUrl}/companies/delete/${companyInvitation.email}`,
    );
  }
  getAllCompanyInvitations(): Observable<CompanyUser[]> {
    this.clearUserCache(); // Clear cache when getting invitations
    return this.http.get<CompanyUser[]>(`${this.apiRootUrl}/companies`, {
      params: { page: 0, limit: 99999 },
    });
  }

  createOrUpdateCompany(
    company: Partial<CompanyUser>,
  ): Observable<CompanyUser> {
    this.clearUserCache(); // Clear cache when creating/updating company
    return this.http.post<CompanyUser>(
      `${this.apiRootUrl}/companies/create`,
      company,
    );
  }

  // Admin-only create/update that does not bind the current user
  adminCreateOrUpdateCompany(
    company: Partial<CompanyUser>,
  ): Observable<CompanyUser> {
    this.clearUserCache();
    return this.http.post<CompanyUser>(
      `${this.apiRootUrl}/admin/companies/create`,
      company,
    );
  }

  validateCompanyEmail(email: string): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(
      `${this.apiRootUrl}/companies/validate-email`,
      { params: { email } },
    );
  }

  // Method to get managed users with caching
  getManagedUsers(): Observable<ManagedUser[]> {
    if (this.managedUsersSubject.getValue().length === 0) {
      return this.loadManagedUsers();
    }
    return this.managedUsers$.pipe(take(1));
  }

  // Private method to load managed users from API
  private loadManagedUsers(): Observable<ManagedUser[]> {
    const query = `
      query {
        getManagedUsers {
          id
          firstName
          lastName
          gender
          birthYear
          zipCode
          categories {
            id
            name
          }
          profilePicture {
            id
            url
          }
        }
      }
    `;

    console.log(query);

    return this.http
      .post<{
        data: {
          getManagedUsers: ManagedUser[];
        };
      }>(this.apiUrl, { query })
      .pipe(
        map((response) => {
          return response.data.getManagedUsers.map((user: ManagedUser) => {
            return new ManagedUser(
              user.id,
              user.firstName,
              user.lastName,
              user.gender,
              user.birthYear,
              user.zipCode,
              user.profilePicture,
              user.categories,
            );
          });
        }),
        tap((users) => this.managedUsersSubject.next(users)),
        shareReplay(1),
      );
  }

  // Method to clear managed users cache
  clearManagedUsersCache() {
    this.managedUsersSubject.next([]);
  }

  createManagedUser(user: {
    firstName: string;
    lastName: string;
    gender?: string;
    birthYear?: number;
    zipCode?: string;
    categoryIds?: string[];
  }): Observable<ManagedUser> {
    const mutation = `
    mutation {
      createManagedUser(
        firstName: "${user.firstName}"
        lastName: "${user.lastName}"
        ${user.gender ? `gender: "${user.gender}"` : ''}
        ${user.birthYear ? `birthYear: ${user.birthYear}` : ''}
        ${user.zipCode ? `zipCode: "${user.zipCode}"` : ''}
        ${
          user.categoryIds && user.categoryIds.length > 0
            ? `categoryIds: [${user.categoryIds
                .map((id) => `"${id}"`)
                .join(', ')}]`
            : ''
        }
      ) {
        id
        firstName
        lastName
        gender
        birthYear
        zipCode
        categories {
          id
          name
        }
        profilePicture
      }
    }`;

    return this.http
      .post<{
        data: {
          createManagedUser: ManagedUser;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(
        map((response) => response.data.createManagedUser),
        tap(() => {
          // Clear the cache to force a refresh
          this.clearManagedUsersCache();
        }),
      );
  }

  updateManagedUser(
    id: string,
    user: {
      firstName?: string;
      lastName?: string;
      gender?: string;
      birthYear?: number;
      zipCode?: string;
      categoryIds?: string[];
    },
  ): Observable<ManagedUser> {
    const mutation = `
    mutation {
      updateManagedUser(
        id: "${id}"
        ${user.firstName ? `firstName: "${user.firstName}"` : ''}
        ${user.lastName ? `lastName: "${user.lastName}"` : ''}
        ${user.gender ? `gender: "${user.gender}"` : ''}
        ${user.birthYear ? `birthYear: ${user.birthYear}` : ''}
        ${user.zipCode ? `zipCode: "${user.zipCode}"` : ''}
        ${
          user.categoryIds && user.categoryIds.length > 0
            ? `categoryIds: [${user.categoryIds
                .map((id) => `"${id}"`)
                .join(', ')}]`
            : ''
        }
      ) {
        id
        firstName
        lastName
        gender
        birthYear
        zipCode
        categories {
          id
          name
        }
        profilePicture
      }
    }`;

    return this.http
      .post<{
        data: {
          updateManagedUser: ManagedUser;
        };
      }>(this.apiUrl, { query: mutation })
      .pipe(
        map((response) => response.data.updateManagedUser),
        tap(() => {
          // Clear the cache to force a refresh
          this.clearManagedUsersCache();
        }),
      );
  }
}
